/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { ClrFormLayout } from '@clr/angular';
import { L10nService } from '@vmw/ngx-vip';
import { PortalInfo } from 'ajs/modules/cportal';
import { AviAlertService } from 'ng/modules/core/services/avi-alert.service';
import * as globalL10n from 'global-l10n';
import * as l10n from './proxy-config-modal.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TPortalInfo = typeof PortalInfo;

/**
 * @description Proxy configuration modal component.
 * @author Shubam Bindal
 */
@Component({
    selector: 'proxy-config-modal',
    templateUrl: './proxy-config-modal.component.html',
})
export class ProxyConfigModalComponent implements OnInit {
    /**
     * PortalInfo instance for proxy configuration.
     */
    @Input()
    public portalInfoItem: PortalInfo;

    /**
     * Emit on cancel.
     */
    @Output()
    public onCancel = new EventEmitter<void>();

    /**
     * Emit on submit.
     */
    @Output()
    public onSubmit = new EventEmitter<void>();

    public readonly l10nKeys = l10nKeys;
    public readonly globalL10nKeys = globalL10nKeys;
    public readonly verticalLayout = ClrFormLayout.VERTICAL;
    public busy = false;
    public error: string;

    constructor(
        @Inject('PortalInfo')
        private readonly PortalInfoClass: TPortalInfo,
        private readonly l10nService: L10nService,
        private readonly aviAlertService: AviAlertService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Check if form is valid.
     */
    public get isFormValid(): boolean {
        const useSplitProxy = this.portalInfoItem?.config?.use_split_proxy;

        if (!useSplitProxy) {
            return true;
        }

        const { host, port } = this.portalInfoItem?.config?.split_proxy_configuration?.getConfig() ?? {};

        return !!(host && port);
    }

    /** @override */
    public ngOnInit(): void {
        if (!this.portalInfoItem) {
            this.portalInfoItem = new this.PortalInfoClass();
            this.portalInfoItem.load();
        }
    }

    public handleCancel(): void {
        this.onCancel.emit();
    }

    public async handleSubmit(): Promise<void> {
        this.busy = true;
        this.error = '';

        try {
            this.clearProxyConfigIfDisabled();

            await this.portalInfoItem.save();
            this.onSubmit.emit();
        } catch (error: any) {
            this.error = error?.data?.error ?? this.l10nService.getMessage(l10nKeys.proxyError);
            this.aviAlertService.throw(this.error);
        } finally {
            this.busy = false;
        }
    }

    /**
     * Clear proxy configuration if use_split_proxy checkbox is unchecked.
     * This set split_proxy_configuration to an empty object when the user has disabled the split proxy feature.
     */
    private clearProxyConfigIfDisabled(): void {
        const useSplitProxy = this.portalInfoItem?.config?.use_split_proxy;

        if (!useSplitProxy && this.portalInfoItem?.config?.split_proxy_configuration) {
            this.portalInfoItem.config.split_proxy_configuration.updateConfig({});
        }
    }
}
